// VirtualFragmentContainerSafe.tsx
import React, { useRef, useEffect, useState } from "react";
import { Fragment } from "../../../interfaces/Publication";
import { ButtonsRight } from "../../../interfaces/UI";
import Anchor from "./Anchor";

interface VirtualFragmentContainerProps {
  fragment: Fragment;
  openButtons: ButtonsRight;
  setOpenButtons: (state: ButtonsRight) => void;
  children: React.ReactNode;
  isHeavy: boolean;
}

const clampScrollIfNeeded = () => {
  const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  if (window.scrollY > maxScroll) {
    window.scrollTo(0, maxScroll);
  }
};

const VirtualFragmentContainer = React.memo(
  ({ fragment, children, isHeavy }: VirtualFragmentContainerProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [isVisible, setIsVisible] = useState(!isHeavy);
    const [isLoaded, setIsLoaded] = useState(!isHeavy);
    const [measuredHeight, setMeasuredHeight] = useState<number>(0);

    const lastValidHeightRef = useRef<number>(0);
    const unloadTimerRef = useRef<number | null>(null);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
      if (!contentRef.current || !isLoaded) return;

      const target = contentRef.current;

      const updateHeight = () => {
        if (!target.isConnected) return;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
          try {
            const rect = target.getBoundingClientRect();
            if (rect.height > 0) {
              const h = Math.ceil(rect.height);
              lastValidHeightRef.current = h;
              setMeasuredHeight(h);
            }
          } catch (err) {
            // Si target fue removido, ignorar
          }
        });
      };

      const ro = new ResizeObserver(updateHeight);
      ro.observe(target);

      updateHeight();

      return () => {
        ro.disconnect();
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }, [isLoaded]);

    useEffect(() => {
      if (!isHeavy || !containerRef.current) return;
      const el = containerRef.current;
      let isComponentMounted = true;

      const io = new IntersectionObserver(
        (entries) => {
          if (!isComponentMounted) return;
          entries.forEach((entry) => {
            if (!isComponentMounted) return;

            if (entry.isIntersecting) {
              if (unloadTimerRef.current) {
                clearTimeout(unloadTimerRef.current);
                unloadTimerRef.current = null;
              }
              setIsVisible(true);
              setIsLoaded(true);
            } else {
              setIsVisible(false);

              if (unloadTimerRef.current) {
                clearTimeout(unloadTimerRef.current);
              }
              unloadTimerRef.current = window.setTimeout(() => {
                if (isComponentMounted) {
                  setIsLoaded(false);
                  setTimeout(() => {
                    clampScrollIfNeeded();
                  }, 0);
                }
              }, 900);
            }
          });
        },
        {
          rootMargin: "300px",
          threshold: 0.01,
        }
      );

      io.observe(el);

      return () => {
        isComponentMounted = false;
        io.disconnect();
        if (unloadTimerRef.current) {
          clearTimeout(unloadTimerRef.current);
          unloadTimerRef.current = null;
        }
      };
    }, [isHeavy]);

    const placeholderHeight = measuredHeight > 0 ? measuredHeight : (lastValidHeightRef.current > 0 ? lastValidHeightRef.current : 400);

    return (
      <div
        ref={containerRef}
        id={`${fragment.type}${fragment.id}`}
        className="virtual-fragment"
        style={{
          minHeight: `${placeholderHeight}px`,
          position: "relative",
          boxSizing: "border-box",
        }}
      >
        {/* AGREGAR ANCHOR PARA SCROLL INTELIGENTE */}
        <Anchor name={`${fragment.type}${fragment.id}`} />
        
        {isLoaded ? (
          <div
            ref={contentRef}
            style={{
              opacity: isVisible ? 1 : 0,
              transition: "opacity 0.18s ease-in-out",
              willChange: "opacity, transform",
            }}
          >
            {children}
          </div>
        ) : (
          <div
            aria-hidden="true"
            style={{
              height: `${placeholderHeight}px`,
              width: "100%",
              background: "#fafafa",
              border: "1px dashed #eee",
              borderRadius: "6px",
              boxSizing: "border-box",
            }}
          />
        )}
      </div>
    );
  }
);

export default VirtualFragmentContainer;